import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography } from '@mui/material';
import _ from 'lodash'; // make sure lodash is imported

// ---- ADS‑B thresholds & helpers ----
const ADSB_THRESHOLDS = {
  distanceNm: 10,   // >10 NM => green regardless of altitude
  majorFt:   400,   // ≤400 -> major (UI only)
  warningFt: 600,   // 401–600 -> warning (UI only)
  advisoryFt:1000,  // 601–1000 -> advisory
};

// Use prod relative URL in prod, absolute URL in dev
async function fetchAdsbLocal() {
  const onProd = /\.?flightiq\.app$/i.test(window.location.hostname);
 // const url = onProd ? '/API/adsb-local.php' : 'https://flightiq.app/API/adsb-local.php';
  const url = 'https://flightiq.app/API/adsb-local.php';

  const res = await fetch(url, { cache: 'no-store' });
  const ct = res.headers.get('content-type') || '';
  const body = await res.text();
  
  if (!ct.includes('application/json')) {
    throw new Error(`Non-JSON from ${url}: ${body.slice(0, 120)}…`);
  }

  const json = JSON.parse(body);
  if (!json || !Array.isArray(json.aircraft)) throw new Error('Invalid ADS-B JSON');
  return json; // { now, messages, aircraft:[...] }
}

// Lightweight elevation lookup (meters) -> OpenTopoData test dataset.
// Swap to prod endpoint later if you want (USGS/Google/etc).
const elevCache = new Map(); // key: `${lat.toFixed(3)},${lon.toFixed(3)}` -> meters|null

async function getGroundElevationMeters(lat, lon) {
  const key = `${lat.toFixed(3)},${lon.toFixed(3)}`;
  if (elevCache.has(key)) return elevCache.get(key);

  const onProd = /\.?flightiq\.app$/i.test(window.location.hostname);
  const url = onProd
    ? `/API/elevation.php?lat=${lat}&lon=${lon}`
    : `https://flightiq.app/API/elevation.php?lat=${lat}&lon=${lon}`;

  try {
    const r = await fetch(url, { cache: 'no-store' });
    const j = await r.json();
    const m = (j && j.ok && typeof j.elevation_m === 'number') ? j.elevation_m : null;
    elevCache.set(key, m);
    return m;
  } catch {
    elevCache.set(key, null);
    return null;
  }
}
function pickClosest(aircraft) {
  let best = null;
  for (const ac of aircraft) {
    if (ac?.r_dst == null) continue; // need distance from receiver
    if (best === null || ac.r_dst < best.r_dst) best = ac;
  }
  return best; // may be null
}

/** UI-level status for this card: 'go' | 'advisory' | 'warning' | 'major' */
function classifyNearestUI(distNm, altAglFt, th = ADSB_THRESHOLDS) {
  if (distNm == null) return 'go';             // no target with distance
  if (distNm > th.distanceNm) return 'go';     // beyond threshold => green
  if (altAglFt == null) return 'advisory';     // unknown AGL near you => soft advisory
  if (altAglFt > th.advisoryFt) return 'go';   // >1000 ft AGL
  if (altAglFt > th.warningFt)  return 'advisory'; // 601–1000
  if (altAglFt > th.majorFt)    return 'warning';  // 401–600
  return 'major';                                   // ≤400
}

/** Compress UI status to app severities (card is informational only) */
function mapUiToAppSeverity(uiStatus) {
  return uiStatus === 'go' ? 'go' : 'advisory';
}

function feet(meters) { return meters * 3.28084; }

function formatNearestSummary({ nearest, distNm, altText }) {
  if (!nearest) return 'No targets with distance available.';
  const flight = (nearest.flight || '').trim() || nearest.hex || 'Unknown';
  const dist   = distNm != null ? `${distNm.toFixed(1)} NM` : 'N/A';
  return `Nearest: ${flight} • ${dist} • ${altText}`;
}
// ---- /helpers ----

/**
 * ADSBAircraftCard
 * Props from Home.js: { lat, lon, refreshTrigger, onUpdate(severity, reasons[]) }
 * - Fires only when refreshTrigger changes (GO/Refresh cycle).
 * - Calls onUpdate with compressed severity ('go' | 'advisory') and a one-line reason.
 */
export default function ADSBAircraftCard({ lat, lon, refreshTrigger, onUpdate }) {
  const [summary, setSummary]   = useState('Air traffic data not collected.');
  const [uiStatus, setUiStatus] = useState('advisory'); // go/advisory/warning/major
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    let aborted = false;
    if (lat == null || lon == null) return;

    (async () => {
      try {
        // 1) Get raw ADS-B and nearest target
        const data = await fetchAdsbLocal();
        const nearest = pickClosest(data.aircraft);
        const distNm  = nearest?.r_dst ?? null;

        // 2) Compute AGL if possible
		const altMslFt = nearest ? (nearest.alt_geom ?? nearest.alt_baro ?? null) : null;
		let altAglFt = null;
		
		// Only fetch elevation if the aircraft is near (<= 15 NM) and we have an altitude
		if (nearest?.lat != null && nearest?.lon != null && altMslFt != null && distNm != null && distNm <= 15) {
		const elevM = await getGroundElevationMeters(nearest.lat, nearest.lon);
		if (elevM != null) {
			altAglFt = Math.max(0, Math.round(altMslFt - (elevM * 3.28084)));
			}
		}


        // 3) Classification
        const ui  = classifyNearestUI(distNm, altAglFt);
        const sev = mapUiToAppSeverity(ui);

        // 4) Summary line (prefer AGL when we have it; fall back to MSL)
        const altText = (altAglFt != null)
          ? `${altAglFt} ft AGL`
          : (altMslFt != null ? `${altMslFt} ft MSL` : 'alt N/A');

        const line = formatNearestSummary({ nearest, distNm, altText });

        if (aborted) return;

        setUiStatus(ui);
        setSummary(line);

        // Notify dashboard (Home wires the 'adsb' key)
        onUpdate?.(sev, [line]);
      } catch (err) {
        if (aborted) return;
        setUiStatus('advisory');
        setSummary(`ADS‑B fetch error: ${String(err)}`);
        onUpdate?.('advisory', ['ADS‑B fetch error']);
      }
    })();

    return () => { aborted = true; };
  }, [lat, lon, refreshTrigger, onUpdate]);

  const title = 'Nearby Aircraft';
  const statusLabel = (
    uiStatus === 'go' ? 'Clear'
    : uiStatus === 'advisory' ? 'Clear With Advisories'
    : uiStatus === 'warning' ? 'Warning'
    : 'Major Warning'
  );


 const borderColor = uiStatus === 'no-go' ? 'red'
                    : uiStatus === 'caution' ? 'orange'
                    : uiStatus === 'advisory' ? 'yellow'
                    : uiStatus === 'go' ? 'green'
                    : 'gray';


  return (
        <Card 
      onClick={() => setExpanded(!expanded)}
      sx={{ cursor: 'pointer', borderLeft: `6px solid ${borderColor}`, mb: 1 }}
    >
      <CardContent>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
          {statusLabel}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1.0 }}>
          {summary}
        </Typography>
		This sample data is only valid near the 19380 area code.
      </CardContent>
    </Card>
  );
}

/**
 * Optional named helper if you later want to trigger from outside the card.
 * Usage: refreshAdsbCard(updateCard, 'adsb')
 */
export async function refreshAdsbCard(updateCard, cardId) {
  const data = await fetchAdsbLocal();
  const nearest = pickClosest(data.aircraft);
  const distNm  = nearest?.r_dst ?? null;

  const altMslFt = nearest ? (nearest.alt_geom ?? nearest.alt_baro ?? null) : null;
  let altAglFt = null;
  if (nearest?.lat != null && nearest?.lon != null && altMslFt != null) {
    const elevM = await getGroundElevationMeters(nearest.lat, nearest.lon);
    if (elevM != null) {
      altAglFt = Math.max(0, Math.round(altMslFt - feet(elevM)));
    }
  }

  const ui  = classifyNearestUI(distNm, altAglFt);
  const sev = mapUiToAppSeverity(ui);
  const altText = (altAglFt != null)
    ? `${altAglFt} ft AGL`
    : (altMslFt != null ? `${altMslFt} ft MSL` : 'alt N/A');
  const line = formatNearestSummary({ nearest, distNm, altText });

  updateCard(
    cardId,
    sev,
    sev,
    line,
    nearest ? JSON.stringify({
      flight: (nearest.flight || '').trim(),
      hex: nearest.hex,
      r_dst_nm: distNm,
      alt_agl_ft: altAglFt,
      alt_msl_ft: altMslFt,
      lat: nearest.lat, lon: nearest.lon,
      gs_kt: nearest.gs, track: nearest.track,
      seen_s: nearest.seen
    }, null, 2) : 'No targets with distance available.'
  );
}
